import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Check, X, Settings2, Trash2, Pencil } from "lucide-react";

const ADD = "__add__";

export default function CategorySelect({ type, value, onChange, testid }) {
  const [opts, setOpts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = () => api.get("/categories", { params: { type } }).then((r) => setOpts(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const all = value && !opts.includes(value) ? [value, ...opts] : opts;

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await api.post("/categories", { type, name: n });
      setName("");
      setAdding(false);
      await load();
      onChange(n);
      toast.success("Kategorija dodana");
    } catch (e) {
      toast.error("Greška pri dodavanju kategorije");
    }
  };

  const rename = async (oldName) => {
    const nn = editVal.trim();
    if (!nn || nn === oldName) { setEditKey(null); return; }
    try {
      await api.put("/categories", { type, old_name: oldName, new_name: nn });
      if (value === oldName) onChange(nn);
      setEditKey(null);
      await load();
      toast.success("Kategorija preimenovana");
    } catch (e) {
      toast.error("Greška pri preimenovanju");
    }
  };

  const remove = async (n) => {
    try {
      await api.delete("/categories", { params: { type, name: n } });
      if (value === n) onChange("");
      await load();
      toast.success("Kategorija obrisana");
    } catch (e) {
      toast.error("Greška pri brisanju");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Select value={value || ""} onValueChange={(v) => (v === ADD ? setAdding(true) : onChange(v))}>
          <SelectTrigger data-testid={testid} className="flex-1"><SelectValue placeholder="Odaberi..." /></SelectTrigger>
          <SelectContent className="bg-popover max-h-72">
            {all.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            <SelectItem value={ADD} className="text-blue-600 font-semibold">
              <span className="flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Nova kategorija...</span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" size="icon" variant="outline" onClick={() => setManageOpen(true)} data-testid={`${testid}-manage`} title="Upravljaj kategorijama">
          <Settings2 className="h-4 w-4" />
        </Button>
      </div>

      {adding && (
        <div className="flex gap-2">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Naziv nove kategorije"
            data-testid={`${testid}-new-input`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          />
          <Button type="button" size="icon" onClick={add} data-testid={`${testid}-new-save`}><Check className="h-4 w-4" /></Button>
          <Button type="button" size="icon" variant="outline" onClick={() => { setAdding(false); setName(""); }}><X className="h-4 w-4" /></Button>
        </div>
      )}

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-card sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid={`${testid}-manage-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">Upravljaj kategorijama</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            {opts.length === 0 && <p className="text-sm text-muted-foreground">Nema kategorija.</p>}
            {opts.map((o) => (
              <div key={o} className="flex items-center gap-2 rounded-md border px-2 py-1.5">
                {editKey === o ? (
                  <>
                    <Input autoFocus value={editVal} onChange={(e) => setEditVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); rename(o); } }}
                      className="h-8" data-testid={`${testid}-rename-input`} />
                    <Button type="button" size="icon" className="h-8 w-8" onClick={() => rename(o)} data-testid={`${testid}-rename-save`}><Check className="h-4 w-4" /></Button>
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditKey(null)}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{o}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditKey(o); setEditVal(o); }} data-testid={`${testid}-rename-${o}`}><Pencil className="h-4 w-4" /></Button>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(o)} data-testid={`${testid}-delete-${o}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
